import { useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { Lock, Mail, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Brand } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const u = await login(email, password);
      navigate(u.role === "admin" ? "/admin" : "/dashboard");
    } catch (ex) {
      setErr(errMsg(ex));
    } finally { setBusy(false); }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="relative hidden flex-col justify-between bg-[#0A1F44] p-12 grid-bg lg:flex">
        <Link to="/" data-testid="login-home-link"><Brand dark /></Link>
        <div>
          <h2 className="font-display text-4xl font-bold leading-tight text-white">Your payments,<br />one secure dashboard.</h2>
          <p className="mt-4 max-w-md text-slate-300">Track collections, chargebacks and payouts with UTR-verified accuracy.</p>
        </div>
        <p className="text-xs text-slate-500">© {new Date().getFullYear()} YesPayBizz</p>
      </div>
      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="rise w-full max-w-sm" data-testid="login-form">
          <Link to="/" className="lg:hidden"><Brand /></Link>
          <h1 className="mt-8 font-display text-3xl font-bold text-[#0A1F44] lg:mt-0">Sign in</h1>
          <p className="mt-2 text-sm text-slate-500">Login to your merchant or admin account</p>
          <div className="mt-8 space-y-4">
            <label className="block">
              <span className="text-sm font-medium text-slate-700">Email</span>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-11 pl-10" placeholder="you@business.com" />
              </div>
            </label>
            <label className="block">
              <span className="text-sm font-medium text-slate-700">Password</span>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input data-testid="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-11 pl-10" placeholder="••••••••" />
              </div>
            </label>
          </div>
          {err && <div data-testid="login-error" className="mt-4 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">{err}</div>}
          <button data-testid="login-submit-btn" disabled={busy} className="mt-6 flex h-11 w-full items-center justify-center gap-2 rounded-md bg-[#2563EB] font-semibold text-white transition-colors hover:bg-[#1D4ED8] disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Sign in
          </button>
          <p className="mt-6 text-center text-xs text-slate-400">Accounts are created by YesPayBizz admin only.</p>
        </form>
      </div>
    </div>
  );
}
